import {
  IHouseStepActivityExpectations,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import { HouseCommonActivityOccurrenceValues } from "../../../../../interfaces/interfaces";
import styled from "styled-components/macro";
import { RadioGroup as RadioGroupOriginal } from "../../../components";

const statePath = WIZARD_HOUSE_STEPS_PATHS.ActivityExpectations;
export const WizardStepActivityExpectations: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<
      TStepPayload<IHouseStepActivityExpectations>
    >(state[statePath] ?? {});
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveActivityExpectations(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveActivityExpectationsPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepActivityExpectationsView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const Root = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
const RadioGroup = styled(RadioGroupOriginal)`
  .ant-radio-wrapper {
    padding: 16px;
  }
`;

export const WizardStepActivityExpectationsView: TWizardStepComponentEditableView<IHouseStepActivityExpectations> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t } = useTranslation("house");

      const activityOccurrenceOptions = HouseCommonActivityOccurrenceValues.map(
        (c) => ({
          value: c,
          label: t(
            `wizard.steps.ActivityExpectations.form.spend_time.options.${c}`
          )
        })
      );

      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );

      return (
        <Root layout="vertical">
          <Form.Item
            label={t(`wizard.steps.ActivityExpectations.form.spend_time.title`)}
            validateStatus={errors?.spend_time ? "error" : ""}
            help={
              errors?.spend_time ? (
                errors?.spend_time ? (
                  t("wizard.errors.generic")
                ) : (
                  ""
                )
              ) : !viewonly ? (
                <div style={{ paddingTop: "8px" }}>
                  {t(
                    `wizard.steps.ActivityExpectations.form.spend_time.description`
                  )}
                </div>
              ) : (
                ""
              )
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {activityOccurrenceOptions.find(
                  (o) => o.value === values.spend_time
                )?.label ?? ""}
              </StepViewOnlyValue>
            ) : (
              <RadioGroup
                value={values.spend_time}
                options={activityOccurrenceOptions}
                onChange={(v) => {
                  setValues({
                    ...values,
                    spend_time: v.target.value
                  });
                }}
                disabled={isReadonly("spend_time")}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(
              `wizard.steps.ActivityExpectations.form.help_tenant.title`
            )}
            validateStatus={errors?.help_tenant ? "error" : ""}
            help={
              errors?.help_tenant ? (
                errors?.help_tenant ? (
                  t("wizard.errors.generic")
                ) : (
                  ""
                )
              ) : !viewonly ? (
                <div style={{ paddingTop: "8px" }}>
                  {t(
                    `wizard.steps.ActivityExpectations.form.help_tenant.description`
                  )}
                </div>
              ) : (
                ""
              )
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {activityOccurrenceOptions.find(
                  (o) => o.value === values.help_tenant
                )?.label ?? ""}
              </StepViewOnlyValue>
            ) : (
              <RadioGroup
                value={values.help_tenant}
                options={activityOccurrenceOptions}
                onChange={(v) => {
                  setValues({
                    ...values,
                    help_tenant: v.target.value
                  });
                }}
                disabled={isReadonly("help_tenant")}
              />
            )}
          </Form.Item>
        </Root>
      );
    }
  );
